import type { FeatureData } from "@/types/spectrum";

/**
 * compoundCategory — 시연용 화합물 위험 카테고리 분류 utility (iter2.1 C1).
 *
 * SMILES substring 매칭 + predicted_substance fallback. **forensic 최종 판정 아님**,
 * 수사관 친화 display helper. 향후 backend Python으로 포팅 시 동일 우선순위 + 동일
 * substring 패턴을 사용해야 결과 일치.
 *
 * 우선순위 (forensic 위험도 순 — Q3 grill 결정):
 *   1. halogenated (할로겐화 의심) — Cl/Br/F (다이옥신 전구체 우려)
 *   2. ketone_aldehyde (케톤·알데히드) — C=O 카르보닐 (자극성/독성)
 *   3. aromatic (방향족·자극성) — 벤젠 고리 (발암 위험)
 *   4. petroleum_alkane (유류계 알칸) — 긴 알킬 사슬 (방화 촉진제)
 *   5. other (기타 휘발성)
 *
 * 중복 매칭 시 위 순서대로 먼저 매칭되는 카테고리 채택. 예:
 *   - Benzaldehyde (O=Cc1ccccc1) → ketone_aldehyde (방향족보다 케톤 우선)
 *   - Benzyl chloride (ClCc1ccccc1) → halogenated (방향족보다 할로겐 우선)
 */

export type CompoundCategory =
  | "halogenated"
  | "ketone_aldehyde"
  | "aromatic"
  | "petroleum_alkane"
  | "other";

const CATEGORY_LABELS: Record<CompoundCategory, string> = {
  halogenated: "할로겐화 의심",
  ketone_aldehyde: "케톤·알데히드",
  aromatic: "방향족·자극성",
  petroleum_alkane: "유류계 알칸",
  other: "기타 휘발성",
};

// SMILES substring 매칭 패턴 (우선순위 순 — 위에서 아래로 첫 매칭 채택)
const PATTERN_RULES: Array<{
  category: CompoundCategory;
  matchSmiles: (smiles: string) => boolean;
  matchName?: (name: string) => boolean;
}> = [
  {
    category: "halogenated",
    matchSmiles: (s) => /Cl|Br|(?<![a-z])F/i.test(s),
    matchName: (n) => /chloride|bromide|fluoride|chloro|bromo|fluoro/i.test(n),
  },
  {
    category: "ketone_aldehyde",
    // 카르보닐 — 양옆에 탄소 있어야 함. CO2(O=C=O)는 양옆 O이므로 매칭 X
    // - C(=O) — 케톤/aldehyde 괄호 표기
    // - O=C[Cc] — 알데히드 (O=Cc1ccccc1 = Benzaldehyde)
    // - [Cc]C=O — 끝 알데히드 (CC=O, C=CC=O = Acrolein)
    matchSmiles: (s) => /C\(=O\)|O=C[Cc]|[Cc]C=O/.test(s),
    matchName: (n) => /ketone|aldehyde|aldehid|al$|one$/i.test(n),
  },
  {
    category: "aromatic",
    // 방향족 SMILES: 소문자 c (aromatic) 4개 이상 — 벤젠 고리 패턴
    matchSmiles: (s) => /c1ccccc1|c1ccc.*cc1|c1cc/.test(s),
    matchName: (n) =>
      /benzene|toluene|xylene|naphthalene|aromatic|styrene|ethylbenz/i.test(n),
  },
  {
    category: "petroleum_alkane",
    // 긴 알킬 사슬 (7+ 탄소 직쇄)
    matchSmiles: (s) => /CCCCCCC/.test(s),
    matchName: (n) =>
      /alkane|n-alkane|pentadecane|hexadecane|decane|nonane/i.test(n),
  },
];

/**
 * 화합물을 단일 카테고리로 분류. 우선순위 순 첫 매칭 채택.
 *
 * @param smiles SMILES 문자열 (없으면 predicted_substance fallback)
 * @param predictedSubstance 화합물명 (SMILES 미매칭 시 사용)
 * @returns CompoundCategory — 매칭 없으면 "other"
 */
export function classifyCompound(
  smiles?: string,
  predictedSubstance?: string,
): CompoundCategory {
  const s = smiles ?? "";
  const n = predictedSubstance ?? "";

  for (const rule of PATTERN_RULES) {
    if (s && rule.matchSmiles(s)) return rule.category;
    if (!s && n && rule.matchName?.(n)) return rule.category;
  }
  return "other";
}

/**
 * 카테고리 한국어 라벨 반환.
 */
export function getCategoryLabel(category: CompoundCategory): string {
  return CATEGORY_LABELS[category];
}

/**
 * features 배열을 카테고리별로 집계. count 내림차순으로 정렬된 Map 반환
 * (insertion order 보존).
 *
 * @returns Map<CompoundCategory, number> — count > 0 카테고리만 포함
 */
export function aggregateCategories(
  features: FeatureData[],
): Map<CompoundCategory, number> {
  const counts = new Map<CompoundCategory, number>();
  for (const f of features) {
    const cat = classifyCompound(f.smiles, f.predicted_substance);
    counts.set(cat, (counts.get(cat) ?? 0) + 1);
  }
  // count 내림차순 정렬 (insertion order 보존을 위해 새 Map 생성)
  const sorted = new Map<CompoundCategory, number>();
  Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .forEach(([cat, count]) => sorted.set(cat, count));
  return sorted;
}
